import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

/**
 * Client-side "render the SVD product card?" mechanism, mirroring `should-use-design-system.ts`.
 *
 * These functions only REFLECT `pageProps.isSvdEnabled` — they do not own the kill-switch. The single
 * gate is server-side: `getServerSideBaseProps` evaluates the `ramp_svd-rollout-web` experiment behind
 * `ENABLE_SVD_FLOW_DISCOVERY` (see `svd.config`) and ships the result as `isSvdEnabled`. `_app` seeds
 * the module state below from that prop; `_document` reflects it onto `<html>` so the browser reads
 * the attribute (hydration-safe — never this module state). When the flow is dark the server always
 * sends `isSvdEnabled = false`, so every reader here returns false. Module state is safe because the
 * pages-router render pass is synchronous.
 *
 * These constants live HERE (not `svd.config`) on purpose: this module is on the client render path
 * (every product-card section reads `shouldUseSvdCard()`), so it must stay free of the statsig
 * experiment registry.
 */
export const DOCUMENT_HTML_DATA_SVD_CARD_ATTR = 'data-svd-card'
export const DOCUMENT_HTML_DATA_SVD_CARD_VALUE = 'enabled'

let svdCardEnabledForRender = false

export function setSvdCardEnabledInMemory(enabled: boolean | undefined | null): void {
  svdCardEnabledForRender = Boolean(enabled)

  if (isBrowser()) {
    if (svdCardEnabledForRender) {
      document.documentElement.setAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR, DOCUMENT_HTML_DATA_SVD_CARD_VALUE)
    } else {
      document.documentElement.removeAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR)
    }
  }
}

export function shouldUseSvdCard(): boolean {
  if (isBrowser()) {
    return document.documentElement.getAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR) === DOCUMENT_HTML_DATA_SVD_CARD_VALUE
  }

  return svdCardEnabledForRender
}

/** Sole reader used by `_document` to stamp `<html>` on the initial SSR pass. */
export function isSvdCardEnabledFromNextData(nextData: {
  props?: { pageProps?: { isSvdEnabled?: boolean } }
}): boolean {
  return Boolean(nextData.props?.pageProps?.isSvdEnabled)
}
